class FlufluHome {
  constructor(root) {
    this.root = root;
    this.filterButtons = [...root.querySelectorAll('[data-fluflu-filter]')];
    this.categoryButtons = [...root.querySelectorAll('[data-fluflu-category-filter]')];
    this.panels = [...root.querySelectorAll('[data-fluflu-panel]')];
    this.dynamicGrids = [...root.querySelectorAll('[data-fluflu-dynamic-grid]')];
    this.dynamicStatuses = [...root.querySelectorAll('[data-fluflu-dynamic-status]')];
    this.productsFooter = root.querySelector('[data-fluflu-products-footer]');
    this.productsLink = root.querySelector('[data-fluflu-products-link]');
    this.dynamicProducts = {};
    this.visibleDynamicCounts = {};
    this.productsLimit = Number.parseInt(root.dataset.flufluProductsLimit, 10) || 16;
    this.activeFilter = 'nature';

    this.filterButtons.forEach((button) => {
      button.addEventListener('click', () => this.applyFilter(button.dataset.flufluFilter));
    });

    this.categoryButtons.forEach((button) => {
      button.addEventListener('click', () => {
        this.applyFilter(button.dataset.flufluCategoryFilter);
        const productSection = root.querySelector('[data-fluflu-products]');
        if (productSection) productSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });

    if (this.productsLink) {
      this.productsLink.addEventListener('click', (event) => this.handleProductsLink(event));
    }

    const requestedFilter = new URLSearchParams(window.location.search).get('fluflu');
    if (requestedFilter && this.panels.some((panel) => panel.dataset.flufluPanel === requestedFilter)) {
      this.applyFilter(requestedFilter);
    }

    this.updateProductsLink();
    this.loadDynamicProducts();
  }

  async loadDynamicProducts() {
    if (!this.dynamicGrids.length || !this.root.dataset.flufluProductsUrl) return;

    try {
      const response = await fetch(this.root.dataset.flufluProductsUrl, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      const availableProducts = (data.products || [])
        .map((product) => {
          const availableVariants = (product.variants || []).filter((variant) => variant.available);
          const cheapestVariant = [...availableVariants].sort((a, b) => Number(a.price) - Number(b.price))[0];
          return { product, variant: cheapestVariant };
        })
        .filter(({ product, variant }) => {
          const handle = (product.handle || '').toLowerCase();
          const type = (product.product_type || '').toLowerCase();
          return variant
            && product.images?.length
            && !handle.includes('presentkort')
            && !handle.startsWith('option-set-')
            && !type.includes('gift card');
        });

      this.dynamicGrids.forEach((grid) => {
        const filter = grid.dataset.flufluDynamicGrid;
        const products = availableProducts
          .filter(({ product, variant }) => this.matchesDynamicFilter(filter, product, variant))
          .sort((a, b) => this.dynamicPriority(filter, a.product) - this.dynamicPriority(filter, b.product));

        this.dynamicProducts[filter] = products;
        this.visibleDynamicCounts[filter] = Math.min(this.productsLimit, products.length);
        this.renderDynamicProducts(filter);

        const status = this.dynamicStatuses.find((item) => item.dataset.flufluDynamicStatus === filter);
        if (!status) return;
        if (products.length) {
          status.hidden = true;
        } else {
          status.textContent = filter === 'under150'
            ? 'Det finns inga produkter under 150 kr i lager just nu.'
            : 'Det finns inga produkter med svamp- eller naturmotiv i lager just nu.';
          status.classList.add('fluflu-home__empty');
        }
      });

      this.updateProductsLink();
    } catch (error) {
      this.dynamicStatuses.forEach((status) => {
        status.textContent = 'Produkterna kunde inte laddas. Prova att uppdatera sidan.';
        status.classList.add('fluflu-home__empty');
      });
    }
  }

  productSearchText(product) {
    return [
      product.title,
      product.handle,
      product.product_type,
      ...(product.tags || []),
    ].filter(Boolean).join(' ').toLocaleLowerCase('sv-SE');
  }

  matchesDynamicFilter(filter, product, variant) {
    if (filter === 'under150') return Number(variant.price) < 150;
    if (filter !== 'nature') return false;

    const text = this.productSearchText(product);
    return FlufluHome.natureKeywords.some((keyword) => text.includes(keyword));
  }

  dynamicPriority(filter, product) {
    if (filter !== 'nature') return 0;
    const text = this.productSearchText(product);
    const matchIndex = FlufluHome.natureKeywords.findIndex((keyword) => text.includes(keyword));
    return matchIndex === -1 ? FlufluHome.natureKeywords.length : matchIndex;
  }

  createProductCard(product, variant) {
    const article = document.createElement('article');
    article.className = 'fluflu-home__product-card';

    const link = document.createElement('a');
    link.className = 'fluflu-home__product-link';
    link.href = `/products/${encodeURIComponent(product.handle)}`;

    const media = document.createElement('div');
    media.className = 'fluflu-home__product-media';

    const image = document.createElement('img');
    image.src = product.images[0].src;
    image.alt = product.title;
    image.loading = 'lazy';
    image.width = product.images[0].width || 800;
    image.height = product.images[0].height || 800;
    media.append(image);

    const badge = document.createElement('span');
    badge.className = 'fluflu-home__badge';
    const tags = (product.tags || []).map((tag) => tag.toLowerCase());
    badge.textContent = tags.some((tag) => tag.includes('handgjord')) ? 'Handgjord' : 'I lager';
    media.append(badge);

    const info = document.createElement('div');
    info.className = 'fluflu-home__product-info';

    const title = document.createElement('h3');
    title.className = 'fluflu-home__product-title';
    title.textContent = product.title;

    const price = document.createElement('div');
    price.className = 'fluflu-home__product-price';
    price.textContent = this.formatPrice(Number(variant.price));

    const compareAtPrice = Number(variant.compare_at_price);
    if (compareAtPrice > Number(variant.price)) {
      const oldPrice = document.createElement('s');
      oldPrice.textContent = this.formatPrice(compareAtPrice);
      price.append(' ', oldPrice);
    }

    info.append(title, price);
    link.append(media, info);
    article.append(link, this.createProductActions(product, variant));
    return article;
  }

  createProductActions(product, variant) {
    const actions = document.createElement('div');
    actions.className = 'fluflu-home__product-actions';

    if ((product.variants || []).length > 1) {
      const chooseLink = document.createElement('a');
      chooseLink.className = 'fluflu-home__product-action fluflu-home__choose-options button';
      chooseLink.href = `/products/${encodeURIComponent(product.handle)}`;
      chooseLink.textContent = 'Välj alternativ';
      chooseLink.setAttribute('aria-label', `Välj alternativ för ${product.title}`);
      actions.append(chooseLink);
      return actions;
    }

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'fluflu-home__product-action fluflu-home__quick-add-button button';
    button.dataset.defaultLabel = 'Lägg i varukorgen';
    button.setAttribute('aria-label', `Lägg ${product.title} i varukorgen`);

    const label = document.createElement('span');
    label.textContent = button.dataset.defaultLabel;

    const spinner = document.createElement('div');
    spinner.className = 'loading__spinner hidden';
    spinner.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66" aria-hidden="true"><circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"></circle></svg>';

    button.append(label, spinner);
    button.addEventListener('click', () => this.addDynamicProductToCart(button, variant.id));
    actions.append(button);
    return actions;
  }

  async addDynamicProductToCart(button, variantId) {
    if (button.getAttribute('aria-disabled') === 'true') return;

    const label = button.querySelector('span');
    const spinner = button.querySelector('.loading__spinner');
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    label.textContent = button.dataset.defaultLabel;
    button.classList.remove('fluflu-home__quick-add-button--error');
    button.setAttribute('aria-disabled', 'true');
    button.classList.add('loading');
    spinner.classList.remove('hidden');

    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      if (cart) {
        formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(button);
      }

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = formData;

      const response = await fetch(window.routes.cart_add_url, config);
      const data = await response.json();

      if (!response.ok) throw new Error(data.description || data.message || 'Produkten kunde inte läggas till.');

      if (cart) {
        try {
          cart.renderContents(data);
        } catch (renderError) {
          console.warn('Produkten lades till, men varukorgsmeddelandet kunde inte visas.', renderError);
        }
      } else {
        window.location = window.routes.cart_url;
        return;
      }

      label.textContent = 'Tillagd ✓';
      button.classList.add('fluflu-home__quick-add-button--success');
      window.setTimeout(() => {
        if (!button.isConnected || button.classList.contains('loading')) return;
        label.textContent = button.dataset.defaultLabel;
        button.classList.remove('fluflu-home__quick-add-button--success');
      }, 1800);
    } catch (error) {
      label.textContent = 'Försök igen';
      button.classList.add('fluflu-home__quick-add-button--error');
    } finally {
      button.classList.remove('loading');
      spinner.classList.add('hidden');
      button.removeAttribute('aria-disabled');
    }
  }

  formatPrice(price) {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: this.root.dataset.flufluCurrency || 'SEK',
      maximumFractionDigits: 0,
    }).format(price);
  }

  renderDynamicProducts(filter) {
    const grid = this.dynamicGrids.find((item) => item.dataset.flufluDynamicGrid === filter);
    const products = this.dynamicProducts[filter] || [];
    const visibleCount = this.visibleDynamicCounts[filter] || this.productsLimit;
    if (!grid) return;

    grid.replaceChildren(
      ...products
        .slice(0, visibleCount)
        .map(({ product, variant }) => this.createProductCard(product, variant)),
    );
  }

  handleProductsLink(event) {
    if (this.activeFilter !== 'nature') return;

    event.preventDefault();
    const products = this.dynamicProducts.nature;
    if (!products?.length) return;

    this.visibleDynamicCounts.nature = Math.min(
      (this.visibleDynamicCounts.nature || this.productsLimit) + this.productsLimit,
      products.length,
    );
    this.renderDynamicProducts('nature');
    this.updateProductsLink();
  }

  updateProductsLink() {
    if (!this.productsFooter || !this.productsLink) return;

    const filter = this.activeFilter;
    const urlKey = `${filter}Url`;
    const labelKey = `${filter}Label`;
    const url = this.productsLink.dataset[urlKey];
    const label = this.productsLink.dataset[labelKey];

    this.productsFooter.hidden = false;
    this.productsLink.href = url || this.productsLink.dataset.allUrl;
    this.productsLink.textContent = label || this.productsLink.dataset.allLabel;

    if (filter === 'children') {
      this.productsFooter.hidden = true;
      return;
    }

    if (filter === 'nature' && this.dynamicProducts.nature) {
      const total = this.dynamicProducts.nature.length;
      const visible = this.visibleDynamicCounts.nature || 0;
      this.productsFooter.hidden = total === 0 || visible >= total;
    }
  }

  applyFilter(filter) {
    this.activeFilter = filter;
    this.panels.forEach((panel) => {
      panel.hidden = panel.dataset.flufluPanel !== filter;
    });

    this.filterButtons.forEach((button) => {
      button.setAttribute('aria-pressed', button.dataset.flufluFilter === filter ? 'true' : 'false');
    });

    this.updateProductsLink();
  }
}

FlufluHome.natureKeywords = [
  'svamp', 'mushroom', 'dark forest', 'skog', 'forest', 'trädgård', 'garden',
  'ormbunke', 'herbarium', 'lav', 'kotte', 'löv', 'leaf', 'växt', 'plant',
  'blom', 'flower', 'tusensköna', 'daisy', 'vildablommor', 'wildflower',
  'nattfjäril', 'fjäril', 'moth', 'bi', 'bee', 'skalbagge', 'beetle',
  'fågel', 'bird', 'groda', 'snigel', 'igelkott', 'ekorre', 'uggla', 'varg',
  'älg', 'monstera', 'kaktus', 'fuchsia',
];

const initFlufluHomes = (scope = document) => {
  scope.querySelectorAll('[data-fluflu-home]').forEach((root) => {
    if (!root.dataset.flufluInitialized) {
      root.dataset.flufluInitialized = 'true';
      new FlufluHome(root);
    }
  });
};

document.addEventListener('DOMContentLoaded', () => initFlufluHomes());
document.addEventListener('shopify:section:load', (event) => initFlufluHomes(event.target));
